/**
 * Which build of the web client this is (backlog D1, 2026-09-23).
 *
 * Identity is the commit sha plus a time; the display string is CalVer from
 * the COMMIT date, the same format keepr-api's /health reports
 * (keepr-api utils/buildInfo.js, SCHEMA.md "Release identity"):
 *
 *     2026.09.23+1baa0e5      UTC YYYY.MM.DD of the committer date, '+',
 *                             the first seven hex of the sha
 *
 * The commit date, not the build time, so the string is a function of the
 * commit: a re-run of the same workflow says the same thing. Seven hex
 * sliced, never `git rev-parse --short` (whose length grows with the repo).
 *
 * WHERE IT COMES FROM. CRA inlines REACT_APP_* at build time, and the Firebase
 * workflow (.github/workflows/firebase-hosting-merge.yml) sets three:
 * REACT_APP_COMMIT (GITHUB_SHA), REACT_APP_COMMITTED_AT (`git log -1
 * --format=%cI`) and REACT_APP_BUILT_AT. A local `npm start` or `npm run
 * build` sets none of them and the version reads "dev" — honest, and no
 * prebuild step writing files into src/ that a test run would then need.
 * When the commit is known but its date is not, the build time stands in for
 * the date; the sha is still the identity.
 *
 * No hand-bumped semver: package.json's 0.1.0 is not read.
 */

export const SHORT_SHA_LENGTH = 7;

/** `YYYY.MM.DD` of an instant, in UTC; null for anything unparseable. */
export function calverDate(when) {
	if (when === null || when === undefined || when === '') return null;
	const d = when instanceof Date ? when : new Date(when);
	if (Number.isNaN(d.getTime())) return null;
	const pad = n => String(n).padStart(2, '0');
	return `${d.getUTCFullYear()}.${pad(d.getUTCMonth() + 1)}.${pad(d.getUTCDate())}`;
}

/**
 * `2026.09.23+1baa0e5`, or null when either half is missing — a version with
 * no sha identifies nothing.
 */
export function formatVersion(committedAt, commit) {
	const date = calverDate(committedAt);
	const sha = typeof commit === 'string' ? commit.trim().toLowerCase() : '';
	if (!date || !/^[0-9a-f]{7,40}$/.test(sha)) return null;
	return `${date}+${sha.slice(0, SHORT_SHA_LENGTH)}`;
}

/** The build info for a given env (process.env at build time, in the app). */
export function readBuildInfo(env = {}) {
	const commit = typeof env.REACT_APP_COMMIT === 'string' && /^[0-9a-f]{7,40}$/i.test(env.REACT_APP_COMMIT.trim())
		? env.REACT_APP_COMMIT.trim().toLowerCase()
		: null;
	const builtAt = calverDate(env.REACT_APP_BUILT_AT) ? new Date(env.REACT_APP_BUILT_AT).toISOString() : null;
	const committedAt = calverDate(env.REACT_APP_COMMITTED_AT) ? new Date(env.REACT_APP_COMMITTED_AT).toISOString() : null;
	return {
		commit,
		shortCommit: commit ? commit.slice(0, SHORT_SHA_LENGTH) : null,
		committedAt,
		builtAt,
		version: formatVersion(committedAt || builtAt, commit) || 'dev',
	};
}

// The three names spelled out rather than `process.env` passed whole, so a
// reader sees exactly which build variables this module depends on.
export const BUILD_INFO = readBuildInfo({
	REACT_APP_COMMIT: process.env.REACT_APP_COMMIT,
	REACT_APP_COMMITTED_AT: process.env.REACT_APP_COMMITTED_AT,
	REACT_APP_BUILT_AT: process.env.REACT_APP_BUILT_AT,
});

export const webVersion = BUILD_INFO.version;

/**
 * The API's version from its /health body, or null for an API that predates
 * release identity (`{ ok: true }` alone) or answered something else.
 */
export function apiVersionFrom(body) {
	if (!body || typeof body !== 'object') return null;
	return typeof body.version === 'string' && body.version.trim() ? body.version.trim() : null;
}

/** The footer line and the clipboard text: `Web … · API …`. */
export function versionLine(web, api) {
	return `Web ${web || 'dev'} · API ${api || 'unknown'}`;
}
